import {dataTestId} from '@hconnect/uikit'
import {Autocomplete, Stack, TextField} from '@mui/material'
import {Controller, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {DepartmentType, UpmTypeBase} from '../../../upm/types/common'
import {Equipment} from '../../../upm/types/equipment'
import {useUpdateDepartmentType} from '../../hooks/upmEntityConfig/useUpdateDepartmentType'
import {ItemDetailsLayout} from '../../plant-setup-item-layout'

const emptyOperationalStatus: UpmTypeBase = {id: '', code: '', name: ''}

export const DepartmenTypeForm = ({
  department,
  departmentTypes,
  operationalStatuses
}: {
  department: Equipment
  departmentTypes: DepartmentType[]
  operationalStatuses: UpmTypeBase[]
}) => {
  const methods = useForm<{
    type: DepartmentType | undefined
    operationalStatus: UpmTypeBase | undefined
  }>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      type: departmentTypes.find((opt) => opt.id === department.typeId),
      operationalStatus:
        operationalStatuses.find((opt) => opt.id === department.statusId) || emptyOperationalStatus
    }
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: {isDirty}
  } = methods

  const {mutate: updateDepartmentType, isPending: isSubmitting} = useUpdateDepartmentType()

  const submit = (data: {
    type: DepartmentType | undefined
    operationalStatus: UpmTypeBase | undefined
  }) => {
    department &&
      data?.type &&
      updateDepartmentType(
        {
          ...department,
          typeId: data.type.id,
          statusId: data.operationalStatus?.id
        },
        {
          onSuccess: () => {
            reset({type: data.type, operationalStatus: data.operationalStatus})
          }
        }
      )
  }

  const {t} = useTranslation()

  return (
    <ItemDetailsLayout
      saveButton={{
        hide: false,
        onClick: handleSubmit(submit),
        disabled: !isDirty,
        ButtonProps: {
          loadingPosition: 'start',
          loading: isSubmitting
        }
      }}
    >
      <Stack maxWidth="500px" spacing={3}>
        <Controller
          name={'type'}
          control={control}
          render={({field: {ref, value, onChange}}) => {
            return (
              <Autocomplete
                {...dataTestId('types-list')}
                options={departmentTypes}
                fullWidth
                disableClearable
                getOptionLabel={(option) => option.name}
                disablePortal
                value={value}
                onChange={(_, newValue) => onChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('plantSetup.departmentType')}
                    variant="outlined"
                    inputRef={ref}
                  />
                )}
              />
            )
          }}
        />
        <Controller
          name={'operationalStatus'}
          control={control}
          render={({field: {ref, value, onChange}}) => {
            return (
              <Autocomplete
                {...dataTestId('operational-status-list')}
                options={[emptyOperationalStatus, ...operationalStatuses]}
                fullWidth
                disableClearable
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                disablePortal
                value={value}
                onChange={(_, newValue) => onChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('plantSetup.operationalStatus')}
                    variant="outlined"
                    inputRef={ref}
                  />
                )}
              />
            )
          }}
        />
      </Stack>
    </ItemDetailsLayout>
  )
}
